import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { supabase } from '../../lib/supabase'; // Supabase 클라이언트 import
import {
  Card, 
  CardContent,
  CardActions,
  TextField,
  Button,
  Typography, 
  Box, 
  CircularProgress,
  Chip,
  LinearProgress,
  Paper,
  Avatar,
  Fade,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Menu,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import {
  LightbulbOutlined as LightbulbIcon,
  School as SchoolIcon,
  BookOutlined as BookIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  NoteAlt as NoteAltIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Lightbulb as HintIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import TagSelector from './TagSelector';
import { getSimilarity, calculateScore } from '../../utils/quizUtils';
import { useSnackbar } from 'notistack';
import { toast } from 'react-hot-toast';
import { updateQuizStats } from '../../api/learningStats';
import { useAuth } from '../../context/AuthContext';
import ExplanationEditor from '../editor/ExplanationEditor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import { Link as RouterLink } from 'react-router-dom';

function DefinitionTabs({ definition, aiDefinition, bestDefinition, kDefinition }) {
  const [tab, setTab] = React.useState(0);
  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="기본 정의" />
        <Tab label="AI 정의" />
        <Tab label="추천 정의" />
        <Tab label="K 정의" />
      </Tabs>
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main',
            minHeight: 48,
            mt: 1
          }}
        >
          {tab === 0 && <Typography>{definition}</Typography>}
          {tab === 1 && <Typography>{aiDefinition || 'AI 정의가 없습니다.'}</Typography>}
          {tab === 2 && <Typography>{bestDefinition || '추천 정의가 없습니다.'}</Typography>}
          {tab === 3 && (
            kDefinition ? (
              <Box className="markdown-table">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {kDefinition}
                </ReactMarkdown>
              </Box>
            ) : (
              <Typography color="text.secondary">K 정의가 없습니다.</Typography>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
}

const QuizCard = ({ 
  quizTerms = [],
  currentTerm,
  sortOption,
  selectedSubject,
  onSubjectChange,
  subjects = [],
  onSortOptionChange,
  term,
  onStart,
  onSubmit,
  onNext,
  onPrevious,
  onSelectTerm,
  hasStarted,
  selectedTerm,
  setSelectedTerm,
  userInput,
  setUserInput,
  canSubmit,
  ...rest
}) => {
  const { 
    terms, 
    wrongAnswers,
    termsLoading,
    wrongAnswersLoading,
    quizMode,
    addToWrongAnswers,
    user,
    currentSubject,
    setSubjects,
    setSnackbar,
  } = useApp();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 사용자 인증 상태 디버깅
  useEffect(() => {
    // console.log('QuizCard - 현재 사용자 상태:', { 
    //   userId: user?.id,
    //   isAuthenticated: !!user,
    //   user: user 
    // });
  }, [user]);

  // 사용자 ID 가져오기
  const userId = user?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [hintLevel, setHintLevel] = useState(0);
  const [matchedKeywords, setMatchedKeywords] = useState([]);
  const [score, setScore] = useState(0);
  const [animation, setAnimation] = useState(false);
  const [error, setError] = useState(null);
  const [hintText, setHintText] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [disableHint, setDisableHint] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [myDefinition, setMyDefinition] = useState('');
  const [saveDefinitionLoading, setSaveDefinitionLoading] = useState(false);
  const [previousAnswers, setPreviousAnswers] = useState([]);
  const [isLoadingPreviousAnswers, setIsLoadingPreviousAnswers] = useState(false);
  const [myDefinitionExpanded, setMyDefinitionExpanded] = useState(false);
  const [previousAnswersExpanded, setPreviousAnswersExpanded] = useState(false);
  const [loadingMyDefinition, setLoadingMyDefinition] = useState(false);
  const [showSaveWrongAnswerButton, setShowSaveWrongAnswerButton] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answerError, setAnswerError] = useState('');
  const [answerResult, setAnswerResult] = useState('');
  const [answerFeedback, setAnswerFeedback] = useState('');
  const [showScore, setShowScore] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin } = useAuth();

  // 과목 선택 메뉴 상태 관리
  const [subjectMenuAnchor, setSubjectMenuAnchor] = useState(null);
  const isSubjectMenuOpen = Boolean(subjectMenuAnchor);

  // 과목 메뉴 열기
  const handleSubjectClick = (event) => {
    setSubjectMenuAnchor(event.currentTarget);
  };

  // 과목 메뉴 닫기
  const handleSubjectMenuClose = () => {
    setSubjectMenuAnchor(null);
  };

  // 과목 선택 처리
  const handleSubjectSelect = (subject) => {
    onSubjectChange(subject);
    // 상위에서 subject 변경 시 quizTerms가 갱신되고, currentTerm도 바뀜
  };

  // quizTerms를 직접 사용하도록 변경
  const filteredAndSortedTerms = quizTerms || [];

  // 초기 용어 선택 로직 수정
  useEffect(() => {
    if (selectedTerm) {
      // console.log('선택된 용어로 설정:', selectedTerm);
      setSelectedTerm(selectedTerm);
      setUserInput('');
      setIsCorrect(null);
      setScore(0);
      setShowHint(false);
      setHintLevel(0);
      setMatchedKeywords([]);
      return;
    }

    // 필터링된 용어가 있을 때 초기 용어 선택
    if (filteredAndSortedTerms.length > 0) {
      // console.log('초기 용어 선택:', {
      //   sortOption,
      //   totalTerms: filteredAndSortedTerms.length
      // });

      let initialTerm;
      switch (sortOption) {
        case 'random':
          // 랜덤순: 무작위로 선택
          initialTerm = filteredAndSortedTerms[Math.floor(Math.random() * filteredAndSortedTerms.length)];
          break;
        case 'alphabet':
        case 'wrong_first':
        default:
          // 알파벳순/오답먼저: 첫 번째 용어 선택
          initialTerm = filteredAndSortedTerms[0];
      }

      // console.log('선택된 초기 용어:', initialTerm?.term);
      setSelectedTerm(initialTerm);
      setUserInput('');
      setIsCorrect(null);
      setScore(0);
      setShowHint(false);
      setHintLevel(0);
      setMatchedKeywords([]);
    }
  }, [filteredAndSortedTerms, selectedTerm, sortOption]);

  // 키워드 매칭 검사
  const checkKeywords = (answer) => {
    if (!currentTerm?.keywords) {
      // console.warn('키워드가 없습니다:', currentTerm?.term);
      return { score: 0, isCorrect: false, matched: [] };
    }
    
    // 키워드가 배열인지 확인하고, 아니면 빈 배열 반환
    if (!Array.isArray(currentTerm.keywords)) {
      // console.warn('키워드가 배열이 아닙니다:', currentTerm.keywords);
      return { score: 0, isCorrect: false, matched: [] };
    }
    
    // console.log('🔍 키워드 매칭 검사:', {
    //   term: currentTerm.term,
    //   keywords: currentTerm.keywords,
    //   answer: answer
    // });
    
    const matched = currentTerm.keywords.filter(keyword => 
      answer.toLowerCase().includes(keyword.toLowerCase())
    );
    
    // console.log('✓ 매칭된 키워드:', matched);
    
    // 점수 계산 (키워드 일치 비율에 힌트 사용 페널티 적용)
    const matchRatio = currentTerm.keywords.length > 0 ? matched.length / currentTerm.keywords.length : 0;
    const baseScore = matchRatio * 100;
    
    // 힌트 레벨에 따른 패널티 적용 (1레벨: 1점, 2레벨: 5점, 3레벨: 20점)
    let hintPenalty = 0;
    if (hintLevel >= 1) hintPenalty += 1;  // 1차 힌트 사용: 1점 차감
    if (hintLevel >= 2) hintPenalty += 5;  // 2차 힌트 사용: 5점 추가 차감
    if (hintLevel >= 3) hintPenalty += 20; // 3차 힌트 사용: 20점 추가 차감
    
    const finalScore = Math.max(0, baseScore - hintPenalty);
    
    // 60점 이상이면 정답으로 처리
    const isCorrect = finalScore >= 60;
    
    // console.log('📊 점수 계산:', {
    //   matchedCount: matched.length,
    //   totalKeywords: currentTerm.keywords.length,
    //   matchRatio,
    //   baseScore,
    //   hintPenalty,
    //   finalScore,
    //   isCorrect
    // });
    
    return { 
      score: finalScore, 
      isCorrect, 
      matched 
    };
  };

  // 힌트 표시 처리
  const handleShowHint = () => {
    if (hintLevel < 3) {
      setHintLevel(prev => prev + 1);
      setShowHint(true);
    }
  };

  // 다음 용어로 이동하는 로직 수정
  const handleNext = () => {
    console.log('handleNext 호출!');
    console.log('filteredAndSortedTerms:', filteredAndSortedTerms);
    console.log('currentTerm:', currentTerm);
    const currentIndex = filteredAndSortedTerms.findIndex(term => term.id === currentTerm?.id);
    let nextTerm;
    switch (sortOption) {
      case 'random':
        const availableTerms = filteredAndSortedTerms.filter(term => term.id !== currentTerm?.id);
        nextTerm = availableTerms.length > 0
          ? availableTerms[Math.floor(Math.random() * availableTerms.length)]
          : filteredAndSortedTerms[Math.floor(Math.random() * filteredAndSortedTerms.length)];
        break;
      case 'alphabet':
      case 'wrong_first':
      default:
        nextTerm = currentIndex === filteredAndSortedTerms.length - 1
          ? filteredAndSortedTerms[0]
          : filteredAndSortedTerms[currentIndex + 1];
    }
    console.log('currentIndex:', currentIndex, 'nextTerm:', nextTerm);
    console.log('onSelectTerm typeof:', typeof onSelectTerm);
    if (nextTerm) {
      setSelectedTerm(nextTerm);
      if (typeof onSelectTerm === 'function') {
        console.log('onSelectTerm 호출, nextTerm:', nextTerm);
        onSelectTerm(nextTerm);
      }
    } else {
      console.log('nextTerm이 없음!');
    }
    setAnimation(false);
    if (typeof onNext === 'function') {
      onNext();
    }
  };

  const handleInputChange = (e) => {
    setUserInput(e.target.value);
    if (!hasStarted && typeof onStart === 'function') {
      onStart();
    }
  };

  const [openDialog, setOpenDialog] = useState(false);

  const handleShowPurchaseDialog = (e) => {
    e.stopPropagation();
    setOpenDialog(true);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) {
      setOpenDialog(true);
      return;
    }
    if (userInput.trim() === '') {
      setAnswerError('답변을 입력해주세요');
      return;
    }
    setIsLoading(true);
    setAnswerError('');
    try {
      const result = checkKeywords(userInput);
      const matchedWords = Array.isArray(result.matched) ? result.matched : [];
      const calculatedScore = result.score;
      const answerIsCorrect = result.isCorrect;
      
      setIsCorrect(answerIsCorrect);
      setMatchedKeywords(matchedWords);
      setScore(calculatedScore);
      setAnswerSubmitted(true);
      setShowAnswer(true);
      
      if (answerIsCorrect) {
        setAnswerResult('correct');
        setAnswerFeedback('정답입니다!');
        setShowHint(false);
      } else {
        setAnswerResult('incorrect');
        setAnswerFeedback('오답입니다. 다시 시도해보세요.');
        setShowHint(true);
        setShowSaveWrongAnswerButton(true);
      }
      
      try {
        await updateQuizStats(answerIsCorrect);
      } catch (statsError) {
        // 에러 처리
      }
            
      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      setAnswerError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
      setShowScore(true);
    }
  };

  // 오답 저장 기능
  const handleSaveWrongAnswer = async () => {
    if (!user || !currentTerm) {
      // console.error('오답 저장 실패: 사용자 또는 용어 정보 없음', { user, currentTerm });
      return;
    }
    
    setIsSaving(true);
    try {
      // console.log('💾 오답 저장 시도:', { 
      //   userId: user.id,
      //   termId: currentTerm.id,
      //   term: currentTerm.term
      // });
      
      // 오답 노트에 저장할 데이터 
      const wrongAnswerData = {
        term_id: currentTerm.id,
        term: currentTerm.term,
        subject: currentTerm.subject,
        definition: currentTerm.definition,
        wrong_answer: userInput,
        hint_level: hintLevel,
        keywords_matched: matchedKeywords.length,
        total_keywords: currentTerm.definition.split(/\s+/).length,
        score: score
      };
      
      // console.log('📋 저장할 오답 데이터:', wrongAnswerData);
      
      const success = await addToWrongAnswers(wrongAnswerData);
      // console.log('📌 오답 저장 결과:', success);
      
      if (success) {
        setSnackbar({
          open: true, 
          message: '오답이 저장되었습니다', 
          severity: 'success'
        });
        setShowSaveWrongAnswerButton(false);
      } else {
        // console.error('오답 저장 실패: addToWrongAnswers 함수가 false 반환');
        throw new Error('오답 저장 실패');
      }
    } catch (error) {
      // console.error('오답 저장 중 오류:', error);
      setSnackbar({
        open: true, 
        message: `오답 저장 중 오류가 발생했습니다: ${error.message}`, 
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 힌트 컴포넌트
  const HintBox = () => {
    if (!showHint || !currentTerm) return null;

    const getHintContent = () => {
      switch (hintLevel) {
        case 1:
          return `첫 번째 키워드: ${currentTerm.keywords?.[0] || ''}`;
        case 2:
          return `모든 키워드: ${currentTerm.keywords?.join(', ') || ''}`;
        case 3:
          return currentTerm.definition;
        default:
          return '';
      }
    };

    return (
      <Paper 
        elevation={0} 
        sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: 'background.light',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'primary.light',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            bgcolor: 'primary.main',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <LightbulbIcon color="primary" sx={{ mt: 0.2 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>
              힌트 레벨 {hintLevel}/3
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getHintContent()}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  // 새로운 함수 추가: 정답 제출 후 이전 오답 불러오기
  const loadPreviousAnswers = async (termId) => {
    if (!userId || !termId) return;
    
    try {
      setIsLoadingPreviousAnswers(true);
      const { data, error } = await supabase
        .from('wrong_answers')
        .select('*')
        .eq('user_id', userId)
        .eq('term_id', termId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setPreviousAnswers(data || []);
    } catch (error) {
      // console.error('이전 오답 불러오기 오류:', error);
      enqueueSnackbar('이전 오답을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setIsLoadingPreviousAnswers(false);
    }
  };
  
  // 새로운 함수 추가: 나만의 정의 저장
  const saveMyDefinition = async () => {
    const termId = currentTerm?.id || currentTerm?._id;
    
    if (!userId || !termId || !myDefinition.trim()) {
      enqueueSnackbar('정의를 입력해주세요.', { variant: 'warning' });
      return;
    }

    setSaveDefinitionLoading(true);
    try {
      const { data: existingDefinition, error: fetchError } = await supabase
        .from('my_definitions')
        .select('*')
        .eq('user_id', userId)
        .eq('term_id', termId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (existingDefinition) {
        // 기존 정의 업데이트
        const { error: updateError } = await supabase
          .from('my_definitions')
          .update({ custom_definition: myDefinition })
          .eq('id', existingDefinition.id);

        if (updateError) throw updateError;
      } else {
        // 새 정의 생성
        const { error: insertError } = await supabase
          .from('my_definitions')
          .insert({
            user_id: userId,
            term_id: termId,
            custom_definition: myDefinition,
            term: currentTerm.term
          });

        if (insertError) throw insertError;
      }

      enqueueSnackbar('나만의 정의가 저장되었습니다.', { variant: 'success' });
      setMyDefinitionExpanded(false);
    } catch (error) {
      // console.error('나만의 정의 저장 중 오류 발생:', error);
      enqueueSnackbar('나만의 정의 저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSaveDefinitionLoading(false);
    }
  };
  
  // 나만의 정의 불러오기
  const loadMyDefinition = async () => {
    const termId = currentTerm?.id || currentTerm?._id;
    
    if (!userId || !termId) return;

    setLoadingMyDefinition(true);
    try {
      const { data, error } = await supabase
        .from('my_definitions')
        .select('custom_definition')
        .eq('user_id', userId)
        .eq('term_id', termId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setMyDefinition(data.custom_definition);
      } else {
        setMyDefinition('');
      }
    } catch (error) {
      // console.error('나만의 정의 불러오기 중 오류 발생:', error);
      enqueueSnackbar('나만의 정의를 불러오는데 실패했습니다.', { variant: 'error' });
    } finally {
      setLoadingMyDefinition(false);
    }
  };
  
  // 정답 제출 후 관련 데이터 로드
  useEffect(() => {
    if (isCorrect !== null && currentTerm) {
      const termId = currentTerm.id || currentTerm._id;
      
      if (termId) {
        // 이전 오답 불러오기
        loadPreviousAnswers(termId);
        // 나만의 정의 불러오기
        loadMyDefinition();
      }
    }
  }, [isCorrect, currentTerm]);

  // Admin 정의 관리 state
  const [adminDefinition, setAdminDefinition] = useState('');
  const [adminAIDefinition, setAdminAIDefinition] = useState('');
  const [adminBestDefinition, setAdminBestDefinition] = useState('');
  const [adminKDefinition, setAdminKDefinition] = useState('');

  useEffect(() => {
    setAdminDefinition(currentTerm?.definition || '');
    setAdminAIDefinition(currentTerm?.ai_definition || '');
    setAdminBestDefinition(currentTerm?.best_definition || '');
    setAdminKDefinition(currentTerm?.k_definition || '');
  }, [currentTerm]);

  function unescapeMarkdown(str) {
    return str.replace(/\\([|\\()>\.\-])/g, '$1');
  }

  const handleAdminSave = async () => {
    if (!currentTerm?.id) return;
    setSaveDefinitionLoading(true);
    const { error } = await supabase
      .from('terms')
      .update({
        definition: adminDefinition,
        ai_definition: adminAIDefinition,
        best_definition: adminBestDefinition,
        k_definition: unescapeMarkdown(adminKDefinition),
      })
      .eq('id', currentTerm.id);
    setSaveDefinitionLoading(false);
    if (!error) {
      enqueueSnackbar('정의가 저장되었습니다.', { variant: 'success', autoHideDuration: 2000 });
    } else {
      enqueueSnackbar('저장 실패: ' + error.message, { variant: 'error', autoHideDuration: 2000 });
    }
  };

  const fullTerm = terms.find(t => t.id === currentTerm?.id) || currentTerm;
  // 검색어 입력 상태
  const [searchTerm, setSearchTerm] = useState('');

  // 전체 terms에서 검색
  const matchedTerms = useMemo(
    () => searchTerm
      ? (terms || []).filter(term =>
          term.term.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [],
    [searchTerm, terms]
  );

  // ...이후에 조건부 return문
  if (!quizTerms || quizTerms.length === 0) {
    return (
      <Card 
        sx={{ 
          minWidth: 275, 
          minHeight: 300, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: 'background.paper',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'info.light', mb: 1 }}>
              <BookIcon sx={{ fontSize: 32, color: 'info.dark' }} />
            </Avatar>
            <Typography variant="h6" color="text.primary" fontWeight={600} textAlign="center">
              선택된 과목에 용어가 없습니다
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              다른 과목을 선택하거나 오답노트를 확인해보세요.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!currentTerm) {
    return (
      <Card 
        sx={{ 
          minWidth: 275, 
          minHeight: 300, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: 'background.paper',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" color="text.secondary" fontWeight={500}>
              문제를 준비하는 중...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // console.log('QuizCard props.onStart:', onStart);

  return (
    <Fade in={true} timeout={300}>
      <Card 
        sx={{ 
          minWidth: 275, 
          minHeight: 300,
          width: '100%',
          maxWidth: '100%',
          mx: 0,
          my: 2,
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease',
          transform: isCorrect === null ? 'none' : (isCorrect ? 'scale(1.02)' : 'scale(0.99)'),
          border: '1px solid',
          borderColor: isCorrect === null 
            ? 'divider' 
            : (isCorrect ? 'success.main' : 'error.main'),
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: isCorrect === null 
              ? 'linear-gradient(90deg, primary.main, secondary.main)'
              : (isCorrect ? 'success.main' : 'error.main'),
            opacity: isCorrect === null ? 1 : 0.8,
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* 상단 컨트롤 바: 과목/정렬 + 검색 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={currentTerm?.subject} 
                size="small" 
                color="primary" 
                variant="outlined"
                onClick={handleSubjectClick}
                sx={{ 
                  borderRadius: '16px', 
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                  }
                }}
              />
              <Menu
                anchorEl={subjectMenuAnchor}
                open={isSubjectMenuOpen}
                onClose={handleSubjectMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    maxHeight: 300,
                    width: 200,
                    borderRadius: 2,
                  }
                }}
              >
                {(subjects || []).map((subject) => (
                  <MenuItem 
                    key={subject}
                    onClick={() => handleSubjectSelect(subject)}
                    selected={currentTerm?.subject === subject}
                    sx={{
                      py: 1,
                      px: 2,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        '&:hover': {
                          bgcolor: 'primary.light',
                        }
                      }
                    }}
                  >
                    {subject}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            {/* 우측: 용어 검색창 */}
            <Box sx={{ position: 'relative' }}>
              <TextField
                label="용어 검색"
                variant="outlined"
                size="small"
                sx={{
                  width: { xs: 140, sm: 200, md: 240 },
                  borderRadius: 2,
                  bgcolor: '#f7f7fa',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#f7f7fa',
                    fontSize: '0.97rem',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.300',
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && matchedTerms.length > 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  bgcolor: 'background.paper',
                  boxShadow: 6,
                  borderRadius: 2,
                  zIndex: 10,
                  mt: 0.5,
                  maxHeight: 220,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  p: 0.5,
                  '&::-webkit-scrollbar': {
                    width: 6,
                    background: '#f7f7fa',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#e0e0e0',
                    borderRadius: 3,
                  },
                }}>
                  {matchedTerms.slice(0, 5).map((term, idx, arr) => (
                    <Box
                      key={term.id}
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.97rem',
                        cursor: 'pointer',
                        px: 1.5,
                        py: 1,
                        borderRadius: idx === 0 ? '8px 8px 0 0' : idx === arr.length - 1 ? '0 0 8px 8px' : 0,
                        '&:hover': { bgcolor: 'primary.light', color: 'primary.main' }
                      }}
                      onClick={() => {
                        onSelectTerm(term);
                        setSearchTerm('');
                      }}
                    >
                      {term.term}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
          
          {/* 과목 정보 및 태그 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isCorrect !== null && (
                <Chip 
                  icon={isCorrect ? <CheckCircleIcon /> : <CancelIcon />} 
                  label={isCorrect ? "정답" : "오답"} 
                  color={isCorrect ? "success" : "error"} 
                  size="small" 
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
          
          {/* 용어 제목 */}
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: isCorrect === null 
                ? 'text.primary' 
                : (isCorrect ? 'success.dark' : 'error.dark'),
            }}
          >
            {currentTerm.term}
          </Typography>
          
          {/* 키워드 정보 */}
          {currentTerm?.keywords && Array.isArray(currentTerm.keywords) && (
            <Box sx={{ my: 3 }}>
              {isCorrect === null ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    필수 키워드: {currentTerm.keywords.length}개
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(matchedKeywords.length / currentTerm.keywords.length) * 100} 
                    sx={{ mb: 2, height: 6, borderRadius: 3 }}
                  />
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    키워드 매칭: {matchedKeywords.length}/{currentTerm.keywords.length}개
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 2 }}>
                    {(currentTerm.keywords || []).map((keyword, index) => (
                      <Chip
                        key={index}
                        label={keyword}
                        size="small"
                        color={matchedKeywords.includes(keyword) ? "success" : "default"}
                        variant={matchedKeywords.includes(keyword) ? "filled" : "outlined"}
                        sx={{ 
                          borderRadius: '16px', 
                          fontWeight: 500,
                          opacity: matchedKeywords.includes(keyword) ? 1 : 0.75
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          
          {/* 정답입력 폼 */}
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              label="답변을 입력하세요"
              placeholder="이 용어에 대한 정의를 작성해주세요..."
              value={userInput}
              onChange={canSubmit ? handleInputChange : () => {}}
              InputProps={{
                readOnly: !canSubmit,
                style: !canSubmit
                  ? { background: '#f5f5f5', cursor: 'not-allowed', color: '#aaa' }
                  : {},
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: !canSubmit ? '#f5f5f5' : 'background.light',
                  fontSize: '0.95rem',
                  color: !canSubmit ? '#aaa' : 'inherit',
                }
              }}
            />
          </Box>

          {/* 안내 메시지: 학습권/정기권 없을 때만 */}
          {!canSubmit && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                BLL 학습권 또는 정기권이 필요합니다.{' '}
                <Link
                  component={RouterLink}
                  to="/pricing"
                  sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  구매페이지로 이동
                </Link>
              </Typography>
            </Alert>
          )}

          <HintBox />

          {/* 결과 요약 */}
          {isCorrect !== null && (
            <Box sx={{ mt: 3 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  bgcolor: isCorrect ? 'success.light' : 'error.light',
                  borderRadius: 2,
                  opacity: 0.9
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: isCorrect ? 'success.dark' : 'error.dark', mb: 2 }}>
                  결과: {score.toFixed(0)}점 {isCorrect ? '(정답)' : '(오답)'}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isCorrect ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                      내가 쓴 답:
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      p: 1.5, 
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isCorrect ? 'success.main' : 'error.main'
                    }}>
                      {userInput}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon color="primary" />
                      올바른 정의:
                    </Typography>
                    <DefinitionTabs
                      definition={fullTerm?.definition}
                      aiDefinition={fullTerm?.ai_definition}
                      bestDefinition={fullTerm?.best_definition}
                      kDefinition={fullTerm?.k_definition}
                    />
                    <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                      ※ 점수는 <b>기본 정의</b>에 의해서만 채점됩니다. <b>기본 정의</b>는 관리자의 판단에 따라 AI 정의 또는 추천 정의로 수정될 수 있습니다.<br />
                      (자신에게 적합한 정의로 학습하세요)
                    </Typography>
                    {isAdmin ? (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed #aaa' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>정의 관리 (Admin 전용)</Typography>
                        <TextField
                          label="기본 정의"
                          value={adminDefinition}
                          onChange={e => setAdminDefinition(e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="AI 정의"
                          value={adminAIDefinition}
                          onChange={e => setAdminAIDefinition(e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="추천 정의"
                          value={adminBestDefinition}
                          onChange={e => setAdminBestDefinition(e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>K 정의 (한국어, 마크다운 지원)</Typography>
                          <ExplanationEditor value={adminKDefinition} onChange={setAdminKDefinition} />
                        </Box>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAdminSave}
                          sx={{ mt: 1 }}
                          disabled={saveDefinitionLoading}
                          startIcon={saveDefinitionLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        >
                          {saveDefinitionLoading ? '저장 중...' : '저장'}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                {hintLevel > 0 && (
                  <Chip
                    icon={<LightbulbIcon />}
                    label={`힌트 사용: -${hintLevel * 10}점`}
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ mt: 2, fontWeight: 500 }}
                  />
                )}

                {showSaveWrongAnswerButton && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      disabled={isSaving}
                      onClick={handleSaveWrongAnswer}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {isSaving ? '저장 중...' : '오답 노트에 저장하기'}
                    </Button>
                  </Box>
                )}
              </Paper>
              {/* 나만의 정의/이전 오답 아코디언 등은 필요시 추가 */}
            </Box>
          )}
        </CardContent>
        <Divider />
        <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleShowHint}
              startIcon={<LightbulbIcon />}
              disabled={hintLevel >= 3 || isLoading || isCorrect !== null}
              size="medium"
              color="primary"
            >
              {showHint ? '다음 힌트' : '힌트 보기'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleNext}
              startIcon={<ArrowForwardIcon />}
              size="medium"
              color="primary"
            >
              다음 용어
            </Button>
          </Box>
          {isCorrect === null ? (
            <Button
              onClick={canSubmit ? handleSubmit : undefined}
              variant="contained"
              disabled={!userInput.trim() || isLoading || !canSubmit}
              endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              size="medium"
              color="primary"
              sx={{
                px: 3,
                ...(canSubmit ? {} : {
                  background: '#eee',
                  color: '#aaa',
                  cursor: 'not-allowed',
                }),
              }}
            >
              제출하기
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              size="medium"
              color={isCorrect ? "success" : "primary"}
              sx={{ px: 3 }}
            >
              다음 용어
            </Button>
          )}
        </CardActions>
      </Card>
    </Fade>
  );
};

QuizCard.defaultProps = {
  setSelectedTerm: () => {},
};

export default React.memo(QuizCard); 